import {
  authService,
  type AdminUserInfo,
  type MenuPermission,
} from '@/entities/auth/api/auth.api';
import { normalizeAuthRole } from '@/shared/utils/authRoles';
import type { LoginGeolocation } from '@/shared/utils/loginGeolocation';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * 세션에 보관하는 로그인 사용자.
 * `/admin/auth/me` 프로필·roles·permissions를 persist한다.
 * role은 roles[0] 파생(표시·가드용).
 */
export interface AuthUser {
  username: string;
  /** 표시·가드용 주 역할 (roles[0] 정규화) */
  role: string;
  roles: string[];
  email: string;
  name: string;
  phone: string;
  department: string;
  passwordChangedAt: string | null;
  permissions: MenuPermission[];
  mustChangePassword: boolean;
  temporaryPasswordExpired: boolean;
}

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  isInitializing: boolean;
  /** email + password 로그인 (feat/admin-email-login) */
  login: (
    email: string,
    password: string,
    geolocation?: LoginGeolocation | null,
  ) => Promise<void>;
  /** 서버 로그아웃 후 로컬 세션 정리. API 실패해도 로컬은 항상 종료한다. */
  logout: () => Promise<void>;
  setLoading: (loading: boolean) => void;
  checkAuth: () => Promise<void>;
  initialize: () => Promise<void>;
  /** me 재조회 후 store 갱신 */
  refreshMe: () => Promise<AuthUser>;
  /**
   * 비밀번호 변경 — 새 토큰 저장 + me 재조회.
   * mustChangePassword 플래그를 해제한다.
   */
  changePassword: (
    currentPassword: string,
    newPassword: string,
  ) => Promise<void>;
  /**
   * 내 정보 인라인 수정 — PATCH /admin/auth/me 후 store 갱신.
   * 변경된 필드만 전달한다.
   */
  updateMyProfile: (patch: { name?: string; phone?: string }) => Promise<void>;
  patchUserProfile: (
    patch: Partial<
      Pick<
        AuthUser,
        'name' | 'phone' | 'passwordChangedAt' | 'mustChangePassword'
      >
    >,
  ) => void;
}

/** me 응답 → AuthUser. 세션에 필요한 필드만 매핑한다. */
export const mapAdminUserToAuthUser = (
  data: AdminUserInfo,
  options?: {
    /** 로그인 응답 등 me에 없는 플래그를 덮어쓸 때 */
    mustChangePassword?: boolean;
    temporaryPasswordExpired?: boolean;
  },
): AuthUser => {
  const roles = (data.roles ?? []).map(normalizeAuthRole).filter(Boolean);
  return {
    username: data.username,
    role: roles[0] ?? '',
    roles,
    email: data.email ?? '',
    name: data.name ?? '',
    phone: data.phone ?? '',
    department: data.department ?? '',
    passwordChangedAt: data.passwordChangedAt ?? null,
    permissions: data.permissions ?? [],
    mustChangePassword:
      options?.mustChangePassword !== undefined
        ? Boolean(options.mustChangePassword)
        : Boolean(data.mustChangePassword),
    temporaryPasswordExpired:
      options?.temporaryPasswordExpired !== undefined
        ? Boolean(options.temporaryPasswordExpired)
        : Boolean(data.temporaryPasswordExpired),
  };
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      isInitialized: false,
      isInitializing: false,

      login: async (
        email: string,
        password: string,
        geolocation?: LoginGeolocation | null,
      ) => {
        set({ isLoading: true });

        try {
          const response = await authService.login({
            email,
            password,
            ...(geolocation
              ? {
                  latitude: geolocation.latitude,
                  longitude: geolocation.longitude,
                }
              : {}),
          });

          const { token, refreshToken, mustChangePassword } = response.data;
          authService.setTokens(token, refreshToken);

          const userResponse = await authService.getMe();
          // mustChangePassword는 로그인 응답 SSOT — me에는 내려오지 않는다
          const user = mapAdminUserToAuthUser(userResponse.data, {
            mustChangePassword: Boolean(mustChangePassword),
          });

          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        try {
          await authService.logout();
        } catch {
          // 네트워크·401 등이어도 클라이언트 로그아웃은 진행
        }

        authService.clearSession();
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      checkAuth: async () => {
        const token = localStorage.getItem('auth-token');

        if (!token) {
          set({ isInitialized: true });
          return;
        }

        try {
          set({ isLoading: true });

          const userResponse = await authService.getMe();
          // me에 플래그가 없으면 persist된 강제변경 상태를 유지한다
          const previous = get().user;
          const me = userResponse.data;
          const user = mapAdminUserToAuthUser(me, {
            mustChangePassword:
              me.mustChangePassword !== undefined
                ? me.mustChangePassword
                : previous?.mustChangePassword,
            temporaryPasswordExpired:
              me.temporaryPasswordExpired !== undefined
                ? me.temporaryPasswordExpired
                : previous?.temporaryPasswordExpired,
          });

          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
            isInitialized: true,
          });
        } catch {
          authService.clearSession();
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            isInitialized: true,
          });
        }
      },

      initialize: async () => {
        const currentState = useAuthStore.getState();

        if (currentState.isInitialized || currentState.isInitializing) {
          return;
        }

        set({ isInitializing: true });

        try {
          await currentState.checkAuth();
          const { user } = useAuthStore.getState();
          if (user?.role) {
            const normalized = normalizeAuthRole(user.role);
            const normalizedRoles = (
              user.roles?.length ? user.roles : [user.role]
            ).map(normalizeAuthRole);
            if (
              normalized !== user.role ||
              !user.roles?.length ||
              normalizedRoles.some((r, i) => r !== user.roles[i])
            ) {
              set({
                user: {
                  ...user,
                  role: normalized,
                  roles: normalizedRoles,
                },
              });
            }
          }
        } finally {
          set({ isInitializing: false });
        }
      },

      refreshMe: async () => {
        const userResponse = await authService.getMe();
        const previous = get().user;
        const me = userResponse.data;
        const user = mapAdminUserToAuthUser(me, {
          mustChangePassword:
            me.mustChangePassword !== undefined
              ? me.mustChangePassword
              : previous?.mustChangePassword,
          temporaryPasswordExpired:
            me.temporaryPasswordExpired !== undefined
              ? me.temporaryPasswordExpired
              : previous?.temporaryPasswordExpired,
        });
        set({ user });
        return user;
      },

      changePassword: async (currentPassword, newPassword) => {
        const response = await authService.changePassword({
          currentPassword,
          newPassword,
        });
        const { token, refreshToken } = response.data;
        authService.setTokens(token, refreshToken);
        const user = await get().refreshMe();
        set({
          token,
          user: { ...user, mustChangePassword: false },
          isAuthenticated: true,
        });
      },

      updateMyProfile: async (patch) => {
        const body: { name?: string; phone?: string } = {};
        if (patch.name !== undefined) body.name = patch.name;
        if (patch.phone !== undefined) body.phone = patch.phone;
        if (Object.keys(body).length === 0) return;

        const response = await authService.updateMe(body);
        // PATCH 응답으로 store 갱신 — 추가 GET /auth/me 호출을 피한다.
        const data = response.data;
        if (data?.username != null && Array.isArray(data.roles)) {
          set({ user: mapAdminUserToAuthUser(data) });
          return;
        }
        const current = get().user;
        if (!current) return;
        set({
          user: {
            ...current,
            ...(patch.name !== undefined ? { name: patch.name } : {}),
            ...(patch.phone !== undefined ? { phone: patch.phone } : {}),
          },
        });
      },

      patchUserProfile: (patch) => {
        set((state) => {
          if (!state.user) return state;
          return {
            user: {
              ...state.user,
              ...(patch.name !== undefined ? { name: patch.name } : {}),
              ...(patch.phone !== undefined ? { phone: patch.phone } : {}),
              ...(patch.passwordChangedAt !== undefined
                ? { passwordChangedAt: patch.passwordChangedAt }
                : {}),
              ...(patch.mustChangePassword !== undefined
                ? { mustChangePassword: patch.mustChangePassword }
                : {}),
            },
          };
        });
      },
    }),
    {
      name: 'auth-storage',
      /**
       * v3: email 로그인·JWT subject 전환.
       * 구 username subject 토큰은 무효이므로 마이그레이션 시 세션을 비운다.
       */
      version: 3,
      migrate: (persistedState, fromVersion) => {
        if (fromVersion < 3) {
          authService.clearSession();
          return {
            user: null,
            token: null,
            isAuthenticated: false,
          } as AuthState;
        }

        const state = persistedState as {
          user?: (AuthUser & Record<string, unknown>) | null;
          token?: string | null;
          isAuthenticated?: boolean;
        };

        const user = state.user;
        if (!user) {
          return state as AuthState;
        }

        return {
          ...state,
          user: {
            username: String(user.username ?? ''),
            role: String(user.role ?? ''),
            roles: Array.isArray(user.roles) ? (user.roles as string[]) : [],
            email: String(user.email ?? ''),
            name: String(user.name ?? ''),
            phone: String(user.phone ?? ''),
            department: String(user.department ?? ''),
            passwordChangedAt:
              typeof user.passwordChangedAt === 'string'
                ? user.passwordChangedAt
                : null,
            permissions: Array.isArray(user.permissions)
              ? (user.permissions as AuthUser['permissions'])
              : [],
            mustChangePassword: Boolean(user.mustChangePassword),
            temporaryPasswordExpired: Boolean(user.temporaryPasswordExpired),
          },
        } as AuthState;
      },
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        isLoading: false,
        isInitialized: false,
        isInitializing: false,
      }),
    },
  ),
);
